
import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { findProductByUniqueId, allProductsList } from '../../mocks/allProducts';
import { useCart } from '../../context/CartContext';

export default function ProductoDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  const product = productId ? findProductByUniqueId(productId) : undefined;

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <i className="ri-error-warning-line text-6xl text-gray-300 mb-4"></i>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Producto no encontrado</h2>
        <p className="text-gray-500 mb-6">El producto que buscas no existe o fue removido.</p>
        <Link to="/" className="px-6 py-3 bg-lime-500 text-black rounded-lg font-semibold hover:bg-lime-400 transition-colors whitespace-nowrap cursor-pointer">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const calculatedPackPrice = product.unitPrice * product.unitsPerPack;

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart({
        id: product.id,
        name: product.name,
        image: product.image,
        packPrice: calculatedPackPrice,
        unitsPerPack: product.unitsPerPack,
      });
    }
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  const handleCotizar = () => {
    const message = `Hola, quiero cotizar el producto: ${product.name} - Precio pack: $${formatPrice(calculatedPackPrice)} - Cantidad: ${quantity} pack(s)`;
    window.open(`https://wa.me/56912345678?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Productos relacionados (misma categoría, excluyendo el actual)
  const relatedProducts = allProductsList
    .filter(p => p.categorySlug === product.categorySlug && p.uniqueId !== product.uniqueId)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-black text-white py-6">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lime-400 text-xl"></i>
              <span className="text-gray-400 text-sm">Volver</span>
            </button>
            <h1 className="text-2xl lg:text-3xl font-bold mt-2">{product.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Link
                to={`/${product.categorySlug}`}
                className="text-lime-400 text-sm hover:underline cursor-pointer"
              >
                {product.category}
              </Link>
              <span className="text-gray-600 text-sm">•</span>
              <span className="text-gray-400 text-sm">Catálogo Mayorista</span>
            </div>
          </div>
          <img
            src="https://static.readdy.ai/image/4161236c74d0ec9b097d0882ccdfeefd/9a712df9f4520947901c304b55b2e414.png"
            alt="ACI Distribución"
            className="h-12 w-auto hidden md:block"
          />
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-lime-600 transition-colors cursor-pointer">Inicio</Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <Link to={`/${product.categorySlug}`} className="hover:text-lime-600 transition-colors cursor-pointer">{product.category}</Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <span className="text-gray-800 font-medium">{product.name}</span>
        </nav>
      </div>

      {/* Product Detail */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="bg-gray-50 rounded-2xl p-8 lg:p-12 flex items-center justify-center border border-gray-100">
            <div className="w-full h-[400px] lg:h-[500px] flex items-center justify-center">
              <img
                src={product.image}
                alt={product.name}
                title={`${product.name} - ${product.category} Mayorista`}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            {/* Category Badge */}
            <div className="mb-4">
              <Link
                to={`/${product.categorySlug}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-lime-100 text-lime-700 rounded-full text-xs font-semibold hover:bg-lime-200 transition-colors cursor-pointer"
              >
                <i className="ri-price-tag-3-line"></i>
                {product.category}
              </Link>
            </div>

            {/* Product Name */}
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              {product.name}
            </h2>

            {/* Pack Info Card */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6 space-y-4 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Información del Pack
              </h3>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center">
                  <i className="ri-box-3-line text-lime-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Unidades por pack</p>
                  <p className="text-lg font-bold text-gray-900">{product.unitsPerPack} unidades</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center">
                  <i className="ri-archive-line text-lime-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Tipo de empaque</p>
                  <p className="text-lg font-bold text-gray-900">{product.packagingType}</p>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-4 mb-8">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                Precios
              </h3>

              {/* Unit Price */}
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <i className="ri-coin-line text-gray-400"></i>
                  <span className="text-gray-600">Precio unitario (referencial)</span>
                </div>
                <span className="text-gray-800 font-semibold text-lg">${formatPrice(product.unitPrice)}</span>
              </div>

              {/* Pack Price - Highlighted */}
              <div className="bg-lime-500 rounded-xl p-5 flex justify-between items-center">
                <div>
                  <p className="text-black/70 text-sm font-medium">Precio pack (mínimo de venta)</p>
                  <p className="text-black text-xs mt-0.5">{product.unitsPerPack} unidades × ${formatPrice(product.unitPrice)}</p>
                </div>
                <span className="text-black font-black text-3xl">${formatPrice(calculatedPackPrice)}</span>
              </div>

              {/* Pallet Price */}
              {product.palletPrice && (
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <i className="ri-stack-line text-lime-500"></i>
                    <span className="text-gray-600">Precio pallet (por unidad)</span>
                  </div>
                  <span className="text-lime-600 font-bold text-lg">${formatPrice(product.palletPrice)}</span>
                </div>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="mb-6">
              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wider block mb-3">
                Cantidad de packs
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line text-xl text-gray-700"></i>
                </button>
                <div className="w-20 h-12 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-bold text-gray-900">{quantity}</span>
                </div>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-xl text-gray-700"></i>
                </button>
                <span className="text-sm text-gray-500 ml-2">
                  Total: <strong className="text-gray-800">${formatPrice(calculatedPackPrice * quantity)}</strong>
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAddToCart}
                className={`flex-1 py-4 rounded-xl font-bold text-lg transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer ${
                  addedToCart
                    ? 'bg-lime-500 text-black'
                    : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                {addedToCart ? (
                  <>
                    <i className="ri-check-line text-xl"></i>
                    ¡Agregado al carrito!
                  </>
                ) : (
                  <>
                    <i className="ri-shopping-cart-line text-xl"></i>
                    Agregar al Carrito
                  </>
                )}
              </button>
              <button
                onClick={handleCotizar}
                className="flex-1 bg-white border-2 border-lime-500 text-lime-600 py-4 rounded-xl font-bold text-lg hover:bg-lime-50 transition-colors whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Cotizar por WhatsApp
              </button>
            </div>

            {/* Notice */}
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <i className="ri-information-line text-amber-500 text-xl mt-0.5"></i>
              <p className="text-amber-800 text-sm">
                Venta mínima por pack. El precio unitario es solo referencial y no corresponde al mínimo de venta.
              </p>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-20">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              Más productos de <span className="text-lime-600">{product.category}</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((related) => (
                <Link
                  key={related.uniqueId}
                  to={`/producto/${related.uniqueId}`}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all group cursor-pointer"
                >
                  <div className="w-full h-48 bg-gray-50 flex items-center justify-center p-4">
                    <img
                      src={related.image}
                      alt={related.name}
                      className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-bold text-gray-900 mb-2 line-clamp-2">{related.name}</h4>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{related.unitsPerPack} un/pack</span>
                      <span className="text-lime-600 font-bold text-sm">${formatPrice(related.unitPrice * related.unitsPerPack)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 mt-20 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-600 text-sm">
          <p>© 2025 ACI Distribución - Abarca y Castillo Distribución SpA. Todos los derechos reservados.</p>
          <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer nofollow" className="text-gray-500 hover:text-lime-600 transition-colors mt-2 inline-block">
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
